/**
 * PostHog loader — consent-gated.
 *
 * PostHog (and especially session_recording) drops fingerprinting / analytics
 * cookies, so it must NOT load until the visitor has accepted the "analytics"
 * category in vanilla-cookieconsent (Art. 22 LSSI-CE + RGPD + Guía AEPD 2023).
 *
 * The module exposes:
 *   - initPostHog()       → idempotent loader; safe to call from onConsent.
 *   - disablePostHog()    → opt-out at runtime and purge any local state.
 */

const POSTHOG_KEY = 'phc_xAvL2Iq4tFmANRE7kzbKwaSqp1HJjN7x48s3vr0CMjs';
const POSTHOG_HOST = 'https://us.i.posthog.com';
const POSTHOG_ASSETS = 'https://us-assets.i.posthog.com';

let loadingPromise = null;

function loadPostHogStub() {
  if (loadingPromise) return loadingPromise;

  loadingPromise = new Promise((resolve, reject) => {
    if (window.posthog && window.posthog.__loaded) {
      resolve(window.posthog);
      return;
    }

    // Minimal queue-stub so callers can safely use posthog.capture() before
    // the real bundle has loaded. PostHog flushes the queue on init.
    const stub = window.posthog || [];
    if (!stub.__SV) {
      window.posthog = stub;
      stub._i = [];
      const methods = (
        'init capture identify register reset opt_in_capturing ' +
        'opt_out_capturing has_opted_in_capturing has_opted_out_capturing ' +
        'startSessionRecording stopSessionRecording captureException'
      ).split(' ');
      methods.forEach((m) => {
        stub[m] = function () {
          stub.push([m].concat(Array.prototype.slice.call(arguments)));
        };
      });
      stub.__SV = 1;
    }

    const s = document.createElement('script');
    s.type = 'text/javascript';
    s.async = true;
    s.crossOrigin = 'anonymous';
    s.src = `${POSTHOG_ASSETS}/static/array.js`;
    s.onload = () => resolve(window.posthog);
    s.onerror = (err) => {
      loadingPromise = null;
      reject(err);
    };
    document.head.appendChild(s);
  });

  return loadingPromise;
}

export async function initPostHog() {
  try {
    const ph = await loadPostHogStub();
    if (ph && typeof ph.init === 'function') {
      ph.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        person_profiles: 'identified_only',
        // Defer session recording unless explicitly desired — recordings are
        // a heavier privacy commitment than plain analytics.
        disable_session_recording: true,
        capture_pageview: true,
        capture_pageleave: true,
        autocapture: true,
        respect_dnt: true,
      });
      ph.opt_in_capturing && ph.opt_in_capturing();
    }
  } catch (err) {
    // Silent fail — analytics must never break the page.
    // eslint-disable-next-line no-console
    console.warn('[posthog] load failed:', err);
  }
}

export function disablePostHog() {
  try {
    if (window.posthog) {
      if (typeof window.posthog.opt_out_capturing === 'function') {
        window.posthog.opt_out_capturing();
      }
      if (typeof window.posthog.stopSessionRecording === 'function') {
        window.posthog.stopSessionRecording();
      }
    }
    // Best-effort cleanup of PostHog cookies / storage.
    document.cookie
      .split(';')
      .map((c) => c.split('=')[0].trim())
      .filter((n) => n.startsWith('ph_'))
      .forEach((name) => {
        document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
      });
    Object.keys(localStorage)
      .filter((k) => k.startsWith('ph_') || k.startsWith('posthog'))
      .forEach((k) => localStorage.removeItem(k));
  } catch (_) {
    /* noop */
  }
}
